// Phase 16-07 Task 2 — Settings → Notifications card container.
//
// Per 16-UI-SPEC.md §Component Inventory + §Visuals + 16-CONTEXT.md §Settings:
//   - Feature-detect: hidden entirely when `browserSupportsWebPush()` is false
//     (no DOM emitted — returns null).
//   - Permission-not-granted (`default` | `denied`): renders only the section
//     heading + Enable CTA (take-two primary Btn) + a single helper sub-text.
//   - Permission-granted: renders the 4 NotificationTriggerToggle rows; the
//     QuietHoursEditor appears below ONLY when at least one trigger is on
//     (per UI-SPEC §Visuals — "renders only if at least one trigger is on").
//   - Save flow is silent-optimistic: pill flips immediately; on
//     `saveNotificationPrefs` rejection the component rolls back the pill and
//     renders an inline <p role="alert"> with `settings.notifications.save_error`.
//
// PHASE-16-SUB-1 (per plan §<action> Task 2 step 1):
// We initialize from DEFAULT_PREFS on every mount rather than GETting server
// state — Phase 16 ships no GET-prefs endpoint, the server remains authoritative
// on write (PATCH /api/push-token, Plan 05). Cost: brief UI mismatch on reload
// if user toggled a default-ON trigger off (they see all-ON until their first
// save round-trips). Known limitation; a later phase MAY add a hydration GET.
//
// Plan 17-07 re-skin: now consumes the shared `.card` recipe from
// pieces.css (Plan 17-01) and tokenized var(--space-*) gaps. The legacy
// `sectionStyle` constant has been replaced with token-driven inline styles.

import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type NotificationPrefs,
  saveNotificationPrefs,
} from '../../net/push-prefs'
import { registerWebPush } from '../../push/register'
import { Btn } from '../components/Btn'
import {
  type NotificationTrigger,
  NotificationTriggerToggle,
} from './NotificationTriggerToggle'
import { QuietHoursEditor, type QuietHoursValue } from './QuietHoursEditor'

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '480px',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
}

const sectionHeading: React.CSSProperties = {
  margin: 0,
  fontFamily: 'Fredoka, sans-serif',
  fontWeight: 700,
  fontSize: 'var(--font-size-14)',
  color: 'var(--color-text-primary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  opacity: 0.9,
}

const TRIGGER_ORDER: readonly NotificationTrigger[] = [
  'opponent_moved',
  'bot_substitution',
  'daily_streak',
  'tournament_cycle',
] as const

/**
 * Default preferences applied on first mount (PHASE-16-SUB-1). All triggers
 * ON per CONTEXT.md §"Default trigger state when permission first granted"
 * + quiet hours 22:00→08:00 in the browser-detected IANA timezone.
 */
function makeDefaultPrefs(): NotificationPrefs {
  let tz = 'UTC'
  try {
    const fmt = new Intl.DateTimeFormat()
    tz = fmt.resolvedOptions().timeZone || 'UTC'
  } catch {
    // Browser environment without Intl tz support — fall back to UTC. The
    // server treats invalid tz as fail-open (deliver) per Phase 16 backend.
    tz = 'UTC'
  }
  return {
    triggers: {
      opponent_moved: true,
      bot_substitution: true,
      daily_streak: true,
      tournament_cycle: true,
    },
    quiet_hours: {
      start_hour: 22,
      end_hour: 8,
      tz,
    },
  }
}

/**
 * Feature-detect — same shape as `register.ts:78-87 browserSupportsWebPush`.
 * Returns true only when both `navigator.serviceWorker` and `window.PushManager`
 * are present (per CONTEXT.md §Settings UI §Unsupported browsers).
 */
function browserSupportsWebPush(): boolean {
  const nav = globalThis.navigator as Navigator | undefined
  if (!nav || !('serviceWorker' in nav) || !nav.serviceWorker) return false
  if (
    typeof (globalThis as { PushManager?: unknown }).PushManager === 'undefined'
  )
    return false
  return true
}

function readNotificationPermission(): NotificationPermission {
  if (typeof Notification === 'undefined') return 'default'
  try {
    return Notification.permission
  } catch {
    return 'default'
  }
}

export function NotificationsCard(): JSX.Element | null {
  // PHASE-16-SUB-1: initialize prefs from DEFAULT_PREFS each mount (no GET).
  const [prefs, setPrefs] = useState<NotificationPrefs>(() =>
    makeDefaultPrefs(),
  )
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    readNotificationPermission(),
  )
  const [saveError, setSaveError] = useState(false)

  const onEnable = useCallback(async () => {
    // Click handler keeps `requestPermission` as the FIRST awaited expression
    // (Pitfall 1 user-gesture invariant — iOS Safari rejects subscribe if the
    // permission prompt is not first inside the click frame).
    let perm: NotificationPermission = 'default'
    try {
      perm = await Notification.requestPermission()
    } catch {
      perm = 'default'
    }
    setPermission(perm)
    if (perm === 'granted') {
      try {
        // WR-04 fix — same skipPermissionPrompt pattern as
        // PushOptInModal.tsx. The caller (this click handler) already
        // resolved Notification.requestPermission() inside the user-gesture
        // frame; registerWebPush should NOT prompt a second time.
        await registerWebPush({ skipPermissionPrompt: true })
      } catch {
        // Best-effort — failure here does not block the UI transition. The
        // user can re-tap Enable to retry. Mirrors Plan-06 PushOptInModal
        // contract.
      }
    }
  }, [])

  const persist = useCallback(
    async (next: NotificationPrefs, previous: NotificationPrefs) => {
      setSaveError(false)
      try {
        await saveNotificationPrefs(next)
      } catch {
        // Optimistic rollback per UI-SPEC §Toast & State Feedback.
        setPrefs(previous)
        setSaveError(true)
      }
    },
    [],
  )

  // WR-05 fix — React setState updaters should be pure functions; StrictMode
  // double-invokes them in dev, which previously caused `persist()` to fire
  // twice per toggle (two PATCH requests with two distinct Idempotency-Keys).
  // The server is whole-field-replace idempotent at the resource level, but
  // (a) operator-side observability lies in dev, (b) a future
  // re-invocation-wrapper would leak to prod, and (c) two concurrent in-
  // flight PATCHes can land out-of-order on retry-with-backoff and Last-
  // Write-Wins a stale value. Hoist the side effect out of the updater:
  // compute the new state synchronously from the current `prefs` ref and
  // call `persist` exactly once.
  const onToggle = useCallback(
    (trigger: NotificationTrigger, next: boolean) => {
      const updated: NotificationPrefs = {
        ...prefs,
        triggers: { ...prefs.triggers, [trigger]: next },
      }
      setPrefs(updated)
      void persist(updated, prefs)
    },
    [prefs, persist],
  )

  const onChangeQuietHours = useCallback(
    (next: QuietHoursValue) => {
      const updated: NotificationPrefs = { ...prefs, quiet_hours: next }
      setPrefs(updated)
      void persist(updated, prefs)
    },
    [prefs, persist],
  )

  // Feature-detect happens at render-time AFTER all hooks (rules-of-hooks: all
  // hooks must be called unconditionally in the same order every render). The
  // test stubs navigator post-mount in some cases; we re-evaluate every render
  // to honour the contract.
  if (!browserSupportsWebPush()) return null

  const anyTriggerOn = Object.values(prefs.triggers).some((v) => v === true)

  const headingNode = (
    <h2 id="notifications-heading" style={sectionHeading}>
      <FormattedMessage
        id="settings.section.notifications"
        defaultMessage="Notifications"
      />
    </h2>
  )

  if (permission !== 'granted') {
    // Permission default OR denied → Enable CTA + helper sub-text only.
    return (
      <section
        className="card"
        aria-labelledby="notifications-heading"
        data-section="notifications"
        data-permission={permission}
        style={cardStyle}
      >
        {headingNode}
        <Btn
          kind="primary"
          size="lg"
          block
          onClick={onEnable}
          testId="enable-notifications"
        >
          <FormattedMessage
            id="settings.notifications.enable_cta"
            defaultMessage="Enable browser notifications"
          />
        </Btn>
        <p
          style={{
            margin: 0,
            fontFamily: 'Manrope, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.5,
          }}
        >
          {permission === 'denied' ? (
            <FormattedMessage
              id="settings.notifications.permission_denied"
              defaultMessage="Permission blocked. Allow it in your browser settings to enable nudges."
            />
          ) : (
            <FormattedMessage
              id="settings.notifications.enable_helper"
              defaultMessage="We'll ask for your browser's permission."
            />
          )}
        </p>
      </section>
    )
  }

  // Permission granted → 4 trigger toggles + (conditional) QuietHoursEditor.
  return (
    <section
      className="card"
      aria-labelledby="notifications-heading"
      data-section="notifications"
      data-permission="granted"
      style={cardStyle}
    >
      {headingNode}
      {TRIGGER_ORDER.map((trigger) => (
        <NotificationTriggerToggle
          key={trigger}
          trigger={trigger}
          selected={prefs.triggers[trigger]}
          onChange={(next) => onToggle(trigger, next)}
        />
      ))}
      {anyTriggerOn && (
        <QuietHoursEditor
          value={prefs.quiet_hours}
          onChange={onChangeQuietHours}
        />
      )}
      {saveError && (
        <p
          role="alert"
          style={{
            margin: 0,
            fontFamily: 'Manrope, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-cta-destructive)',
            lineHeight: 1.5,
          }}
        >
          <FormattedMessage
            id="settings.notifications.save_error"
            defaultMessage="Couldn't save. Try again in a moment."
          />
        </p>
      )}
    </section>
  )
}
